import * as React from "react";
import { cn } from "./cn";

/**
 * Skeleton block with a sweeping shimmer highlight.
 *
 * The base color is a soft gray; an absolutely-positioned gradient
 * slides left-to-right on top to convey "loading" more strongly than
 * a flat pulse.
 */
export function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[12px] bg-smoke/80",
        className,
      )}
      {...props}
    >
      <div
        aria-hidden
        style={{ animationDelay: "var(--shimmer-delay, 0ms)" }}
        className={cn(
          "absolute inset-0 -translate-x-full animate-skeleton-shimmer",
          "bg-gradient-to-r from-transparent via-white/70 to-transparent",
        )}
      />
    </div>
  );
}

export function SkeletonText({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            "h-3.5 rounded-md",
            i === lines - 1 ? "w-2/3" : "w-full",
          )}
        />
      ))}
    </div>
  );
}

export function SkeletonStat({ className }: { className?: string }) {
  return (
    <div
      className={cn("rounded-xl border border-ink/10 bg-paper p-4", className)}
    >
      <Skeleton className="h-3 w-20 rounded-md" />
      <Skeleton className="mt-3 h-7 w-24 rounded-md" />
    </div>
  );
}

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={cn("rounded-xl border border-ink/10 bg-paper p-5", className)}
    >
      <Skeleton className="h-4 w-1/3 rounded-md" />
      <SkeletonText lines={2} className="mt-4" />
    </div>
  );
}

/**
 * Table-shaped skeleton. Mimics header + N rows with cell-shaped blocks
 * so users perceive the table arriving rather than a single grey slab.
 */
export function SkeletonTable({
  rows = 6,
  cols = 4,
  className,
}: {
  rows?: number;
  cols?: number;
  className?: string;
}) {
  // Stable per-cell width fractions: prevents perfectly-aligned bars from
  // looking like a placeholder grid. Deterministic so SSR/CSR match.
  const cellWidth = (r: number, c: number) => {
    const widths = ["55%", "70%", "45%", "85%", "60%", "75%", "40%", "65%"];
    return widths[(r * 3 + c) % widths.length];
  };

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-smoke bg-paper",
        className,
      )}
    >
      {/* Header row */}
      <div
        className="grid gap-4 border-b border-smoke bg-mist/60 px-4 py-3.5"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-3 w-20 rounded-md bg-smoke" />
        ))}
      </div>
      {/* Body rows — slight stagger via animation-delay for liveliness */}
      <div className="divide-y divide-smoke/70">
        {Array.from({ length: rows }).map((_, r) => (
          <div
            key={r}
            className="grid gap-4 px-4 py-4"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: cols }).map((_, c) => (
              <Skeleton
                key={c}
                className="h-3.5 rounded-md"
                style={
                  {
                    width: cellWidth(r, c),
                    "--shimmer-delay": `${(r * cols + c) * 60}ms`,
                  } as React.CSSProperties
                }
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
